class SearchArticles extends HTMLElement {
  constructor() {
    super();
    this.endpoint = 'https://myline24.de/api/2026-01/graphql.json';
    this.token = 'f670fe0536b95e399f03f23b45f0f3f4';
  }

  static escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  static normalizeText(str) {
    if (typeof str !== 'string') return '';
    return str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.resultsContainer = document.querySelector('[js-articles-grid]');
    this.paginationEl = document.querySelector('[js-pagination]');

    if (!this.form || !this.resultsContainer) return;

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = this.form.querySelector('input[name="query"]');
      const query = input?.value?.trim().toLowerCase();
      if (query) {
        this.searchArticles(query);
      }
    });
  }

  buildGraphQLQuery() {
    return `
      query($first: Int!, $after: String, $query: String) {
        articles(first: $first, after: $after, query: $query) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              title
              handle
              tags
              blog {
                handle
              }
              image {
                url
                altText
              }
              metafields(identifiers: [
                { namespace: "custom", key: "recipe_ingredients" },
                { namespace: "custom", key: "recipe_calories" }
              ]) {
                key
                value
                reference {
                  ... on Metaobject {
                    fields {
                      key
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
  }

  async fetchAllArticles() {
    const tagged = location.pathname.includes('tagged');
    const tag = tagged ? this.getAttribute('data-tag-name') : null;
    const query = this.buildGraphQLQuery();

    const variables = {
      first: 250,
      after: null,
      query: tag ? `tag:'${tag}'` : null,
    };

    const allEdges = [];
    let hasNextPage = true;
    let iterations = 0;

    while (hasNextPage) {
      if (++iterations > 20) break;

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json = await response.json();

      if (json.errors) {
        throw new Error(json.errors.map(e => e.message).join(', '));
      }

      const articles = json.data?.articles;
      if (!articles) {
        throw new Error('No articles data in response');
      }

      allEdges.push(...articles.edges);
      hasNextPage = articles.pageInfo.hasNextPage;
      variables.after = articles.pageInfo.endCursor;
    }

    return allEdges;
  }

  async searchArticles(searchTerm) {
    try {
      const edges = await this.fetchAllArticles();

      const articles = edges.map(edge => {
        const node = edge.node;
        const metafields = node.metafields || [];

        const ingredientsMeta = metafields.find(mf => mf?.key === 'recipe_ingredients');
        const caloriesMeta = metafields.find(mf => mf?.key === 'recipe_calories');

        const refFields = ingredientsMeta?.reference?.fields || [];
        const nutritionalField = refFields.find(f => f.key === 'nutritional_value_text');
        const nutritionalText = SearchArticles.normalizeText(nutritionalField?.value ?? '');

        return {
          ...node,
          nutritional_value_text: nutritionalText,
          recipe_calories: caloriesMeta?.value || '',
        };
      });

      const normalizedTerm = SearchArticles.normalizeText(searchTerm);

      const filtered = articles.filter(article => {
        const titleText = SearchArticles.normalizeText(article.title ?? '');
        return article.nutritional_value_text.includes(normalizedTerm) ||
          titleText.includes(normalizedTerm);
      });

      this.renderResults(filtered);
      this.paginationEl?.classList.add('hidden');
    } catch (error) {
      console.error('SearchArticles: Failed to search articles', error);
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = '<p>Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.</p>';
      }
    }
  }

  renderResults(articles) {
    if (!this.resultsContainer) return;

    if (!articles.length) {
      this.resultsContainer.innerHTML = '<p>No results found.</p>';
      this.resultsContainer.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    const esc = SearchArticles.escapeHtml;

    this.resultsContainer.innerHTML = articles.map(article => {
      const imageUrl = article.image?.url;
      const altText = article.image?.altText || article.title;
      const blogHandle = article.blog?.handle;
      const calories = article.recipe_calories;
      const articleUrl = `/blogs/${encodeURIComponent(blogHandle)}/${encodeURIComponent(article.handle)}`;

      return `
        <li class="blog__article-item animation-fade-up-reveal">
          ${imageUrl ? `
            <a href="${esc(articleUrl)}" class="hover">
              <div class="image blog__article-image">
                <img src="${esc(imageUrl)}" alt="${esc(altText)}" class="image__img" loading="lazy" />
              </div>
            </a>` : `
            <div class="blog__article-image placeholder-image">
              <svg class="placeholder-svg" width="100%" height="100%">
                <rect width="100%" height="100%" fill="#ccc"></rect>
              </svg>
            </div>`}
          <div class="blog__meta ${!imageUrl ? 'blog__meta--no-image' : ''}">
            <div class="custom-blog__title--wrap">
              <h3 class="blog__article-title ff-heading ta-c">
                <a href="${esc(articleUrl)}">${esc(article.title)}</a>
              </h3>
              ${calories ? `
                <span class="custom-blog__recipes--icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="9" viewBox="0 0 8 9" fill="none">
                    <circle cx="4" cy="4.5" r="4" fill="#161616"/>
                  </svg>
                </span>
                <div class="custom-blog__detail--wrap">
                  <span class="custom-blog__text">${esc(calories)}</span>
                </div>` : ''}
            </div>
          </div>
        </li>
      `;
    }).join('');

    this.resultsContainer.scrollIntoView({ behavior: 'smooth' });
  }
}

if (!customElements.get('search-articles')) {
  customElements.define('search-articles', SearchArticles);
}
